import {
  BuilderComponentPlugin,
  BuilderComponentPluginFactory,
  ChunkType,
  FileType,
  HandlerSet,
  ICodeStruct,
  IContainerInfo,
  IScope,
  NodeGeneratorConfig,
  PIECE_TYPE,
} from '../../../types';

import { REACT_CHUNK_NAME } from './const';
import { COMMON_CHUNK_NAME, FUNCTION_DEFINE_CHUNK_NAME } from '../../../const/generator';

import { createReactFunctionNodeGenerator } from '../../../utils/nodeToJSX';
import { Scope } from '../../../utils/Scope';
import { IPublicTypeJSExpression } from '@gant-lowcode/lowcode-types';
import { generateExpression } from '../../../utils/jsExpression';
import { transformJsExpr } from '../../../core/jsx/handlers/transformJsExpression';
import { transformThis2Context } from '../../../core/jsx/handlers/transformThis2Context';
import { generateCompositeType } from '../../../utils/compositeType';
import { mergeImportMaps } from '../../../utils/mergeImportMaps';

export interface PluginConfig {
  fileType?: string;
  nodeTypeMapping?: Record<string, string>;
  fragmentAttrFilter?: boolean;
}

const pluginFactory: BuilderComponentPluginFactory<PluginConfig> = (config?) => {
  const cfg = {
    fileType: FileType.TSX,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    nodeTypeMapping: {} as Record<string, string>,
    ...config,
  };

  const { nodeTypeMapping, fragmentAttrFilter } = cfg;

  const plugin: BuilderComponentPlugin = async (pre: ICodeStruct) => {
    const next: ICodeStruct = {
      ...pre,
    };

    const { tolerateEvalErrors = true, evalErrorsHandler = '' } = next.contextData;
    const imports: Record<
      string,
      {
        defaultImport: string | null;
        namespaceImport: string | null;
        namedImports: Set<string>;
      }
    > = {};
    // 这里会将内部的一些子上下文的访问(this.xxx)转换为 __$$context.xxx 的形式
    // 与 Rax 所不同的是，这里不会将最顶层的 this 转换掉
    const customHandlers: HandlerSet<string> = {
      expression(input: IPublicTypeJSExpression, scope: IScope, config) {
        const expr = generateExpression(input, scope);
        return transformJsExpr(expr, scope, {
          dontWrapEval: !(config?.tolerateEvalErrors ?? tolerateEvalErrors),
          dontTransformThis2ContextAtRootScope: true,
        });
      },
      function(input, scope: IScope) {
        if (input.imports) {
          input.imports.forEach((dep: any) => {
            const existing = imports[dep.package] || {
              defaultImport: null,
              namespaceImport: null,
              namedImports: new Set<string>(),
            };
            // 处理不同导入类型
            if (dep.default) {
              existing.defaultImport = dep.importName;
            } else if (dep.namespace) {
              existing.namespaceImport = dep.importName;
            } else {
              existing.namedImports.add(dep.importName);
            }
            imports[dep.package] = existing;
          });
        }

        return transformThis2Context(
          generateCompositeType(
            {
              type: 'JSFunction',
              value: input.value || 'function () {}',
            },
            Scope.createRootScope(),
          ),
          scope,
          { ignoreRootScope: true },
        );
      },
    };

    // 创建 Fragment属性过滤器
    const createFragmentAttrFilter = (tagMappingFn: (name: string) => string) => {
      return (nodeItem: any, scope: IScope, config?: any, next?: any) => {
        const pieces = next ? next(nodeItem, scope, config) : [];

        // 获取实际的 tag 名称
        const tagName = tagMappingFn(nodeItem.componentName);

        // 如果不是 Fragment，直接返回
        if (tagName !== 'React.Fragment' && tagName !== 'Fragment') {
          return pieces;
        }

        //过掉非 key 的属性
        return pieces.filter((piece: any) => {
          if (piece.type === PIECE_TYPE.ATTR) {
            //只保留 key属性
            return piece.name === 'key';
          }
          return true;
        });
      };
    };

    const generatorPlugins: NodeGeneratorConfig = {
      handlers: customHandlers,
      tagMapping: (v) => nodeTypeMapping[v] || v,
      tolerateEvalErrors,
    };

    // 如果启用了 Fragment属性过滤
    if (fragmentAttrFilter) {
      generatorPlugins.nodePlugins = [createFragmentAttrFilter((v) => nodeTypeMapping[v] || v)];
    }

    if (next.contextData.useRefApi) {
      generatorPlugins.attrPlugins = [
        (attrData, scope, pluginCfg, nextFunc) => {
          if (attrData.attrName === 'ref') {
            return [
              {
                name: attrData.attrName,
                value: `this._refsManager.linkRef('${attrData.attrValue}')`,
                type: PIECE_TYPE.ATTR,
              },
            ];
          }

          return nextFunc ? nextFunc(attrData, scope, pluginCfg) : [];
        },
      ];
    }

    const generator = createReactFunctionNodeGenerator(generatorPlugins);

    const ir = next.ir as IContainerInfo;
    const scope: IScope = Scope.createRootScope();
    const jsxContent = generator(ir, scope);

    next.chunks.push({
      type: ChunkType.STRING,
      fileType: cfg.fileType,
      name: REACT_CHUNK_NAME.FunctionRenderJSX,
      content: `return ${jsxContent};`,
      linkAfter: [FUNCTION_DEFINE_CHUNK_NAME.Start, FUNCTION_DEFINE_CHUNK_NAME.InsStatement],
    });

    // next.chunks.push({
    //   type: ChunkType.STRING,
    //   fileType: cfg.fileType,
    //   name: COMMON_CHUNK_NAME.CustomContent,
    //   content: [
    //     tolerateEvalErrors &&
    //       `
    //       function __$$eval(expr) {
    //         try {
    //           return expr();
    //         } catch (error) {
    //           ${evalErrorsHandler}
    //         }
    //       }

    //       function __$$evalArray(expr) {
    //         const res = __$$eval(expr);
    //         return Array.isArray(res) ? res : [];
    //       }
    //   `,
    //   ]
    //     .filter(Boolean)
    //     .join('\n'),
    //   linkAfter: [COMMON_CHUNK_NAME.FileExport],
    // });
    next.depNames = Object.keys(imports);
    next.imports = mergeImportMaps({}, next.imports, imports);

    // Remove imports for components mapped to Fragment (e.g. Page -> Fragment means Page is unused)
    const fragmentMappedNames = new Set(
      Object.entries(nodeTypeMapping)
        .filter(([_, mapped]) => mapped === 'Fragment')
        .map(([original]) => original),
    );
    if (fragmentMappedNames.size > 0) {
      for (let i = next.chunks.length - 1; i >= 0; i--) {
        const chunk = next.chunks[i];
        if (chunk.name !== COMMON_CHUNK_NAME.ExternalDepsImport) continue;
        const content = String(chunk.content).trim();
        const namedMatch = content.match(
          /^import\s+(?:(\w+)\s*,\s*)?\{([^}]*)\}\s+from\s+['"]([^'"]*)['"]\s*;?$/,
        );
        if (!namedMatch) continue;
        const defaultImport = namedMatch[1] || null;
        const names = namedMatch[2].split(',').map((s) => s.trim()).filter(Boolean);
        const filtered = names.filter((n) => !fragmentMappedNames.has(n));
        if (filtered.length === names.length) continue;
        if (filtered.length === 0 && !defaultImport) {
          next.chunks.splice(i, 1);
        } else {
          const namedPart = filtered.length > 0 ? `{ ${filtered.join(', ')} }` : '';
          const importParts = [defaultImport, namedPart].filter(Boolean).join(', ');
          next.chunks[i] = { ...chunk, content: `import ${importParts} from '${namedMatch[3]}';` };
        }
      }
    }

    return next;
  };
  return plugin;
};

export default pluginFactory;
